import { Form } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthenticateByEmailAccessCodeMutation } from '@app/api/Authentication';
import {
  ACCESS_RIGHTS_PROP,
  ACCESS_TOKEN,
  CALENDAR_PATH,
} from '@app/constants';
import { handleCatchError } from '@app/services';
import {
  setAccessRights,
  setOrganizations,
  setUser,
  useAppDispatch,
} from '@app/store';

import type { AccessCodeFormValues } from '@pages/Authorization/Authorization.types';
import { adaptUserToRedux } from '@pages/Authorization/utils/adaptUserToRedux';
import {
  persistAuthorizationAccessToken,
  persistAuthorizationCookies,
} from '@pages/Authorization/utils/authorizationSession';

export interface UseAuthorizationAccessCodeProps {
  accessCodeValue: string;
  email: string;
  isEnabled: boolean;
}

export const useAuthorizationAccessCode = ({
  accessCodeValue,
  email,
  isEnabled,
}: UseAuthorizationAccessCodeProps) => {
  const [form] = Form.useForm<AccessCodeFormValues>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [serverError, setServerError] = useState('');
  const [isSubmitLoader, setSubmitLoader] = useState(false);

  const [authenticateByEmailAccessCode] =
    useAuthenticateByEmailAccessCodeMutation();

  useEffect(() => {
    if (!isEnabled || !accessCodeValue) {
      return;
    }

    form.setFieldsValue({ code: accessCodeValue });
  }, [accessCodeValue, form, isEnabled]);

  const clearServerErrorHandler = useCallback(() => {
    setServerError('');
  }, []);

  const codeChangeHandler = useCallback(() => {
    if (serverError) {
      setServerError('');
    }
  }, [serverError]);

  const finishHandler = useCallback(
    async (values: AccessCodeFormValues) => {
      try {
        setSubmitLoader(true);
        const user = await authenticateByEmailAccessCode({
          accessCode: values.code,
          email,
        }).unwrap();

        dispatch(setUser(adaptUserToRedux(user)));
        dispatch(setAccessRights(user[ACCESS_RIGHTS_PROP]));
        dispatch(setOrganizations(user.Organizations));
        persistAuthorizationCookies(user);
        persistAuthorizationAccessToken(user[ACCESS_TOKEN]);
        navigate(CALENDAR_PATH);
      } catch (error) {
        setServerError(handleCatchError(error));
      } finally {
        setSubmitLoader(false);
      }
    },
    [authenticateByEmailAccessCode, dispatch, email, navigate]
  );

  return {
    clearServerErrorHandler,
    codeChangeHandler,
    finishHandler,
    form,
    isSubmitLoader,
    serverError,
  };
};
